import { BudgetCategory, BudgetSummary, VendorBudgetItem, Payment } from '../types/ui/Budget';
import { DEFAULT_BUDGET_CATEGORY_SEEDS } from '../constants/budgetCategories';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete , planningPath } from '../services/PlanningApiClient';
import { ApiError } from './apiErrors';
import { BUDGET_ENABLED } from '../constants/featureFlags';
import { getActiveProjectId } from './session';

// ── In-memory cache (no localStorage for app data) ──
let budgetCache: BudgetCategory[] | null = null;
/** The budget's own currency, as the API last reported it (null = the project has not set one). */
let budgetCurrency: string | null = null;
/** True once the API answered 403: this role may not see money, so nothing more is asked. */
let budgetBlocked = false;
/** The project the cache and the 403 belong to: a project switch must not inherit either. */
let cacheProject: string | null = null;

function syncProject(): void {
  const current = getActiveProjectId();
  if (current !== cacheProject) {
    budgetCache = null;
    budgetCurrency = null;
    budgetBlocked = false;
    cacheProject = current;
  }
}

export const BUDGET_CHANGE_EVENT = 'anojeje_budget_change';

/** The currency every budget figure is in; null when the project has none yet (screens say "currency not set"). */
export function getBudgetCurrency(): string | null {
  syncProject();
  return budgetCurrency;
}

/** True when the API refused the budget to this person (a contractor or a financially blind user). */
export function isBudgetBlocked(): boolean {
  syncProject();
  return budgetBlocked;
}

/** Test hook: forget everything cached. */
export function resetBudgetCache(): void {
  budgetCache = null;
  budgetCurrency = null;
  budgetBlocked = false;
  cacheProject = getActiveProjectId();
}

// ── API response shapes (snake_case from the backend, kal shape: uuid ids, exact decimals as strings) ──

interface ApiBudgetPayment {
  id: string;
  budget_item_id: string;
  amount: string | number;
  date: string | null;
  method: string | null;
  status: string | null;
  reference: string | null;
  paid_by: string | null;
  notes: string | null;
  type: string | null;
  refund_of_payment_id: string | null;
  currency: string | null;
}

interface ApiBudgetItem {
  id: string;
  category_id: string;
  vendor_id: string | null;
  vendor_name: string | null;
  service: string | null;
  planned_cost: string | number | null;
  actual_cost: string | number | null;
  /** The budget's currency (the API puts it on every line). */
  currency?: string | null;
  /** Completed payments in another currency than the budget's: left out of actual_cost, counted here. */
  unconverted_payments?: number;
  payment_status: string;
  contract_signed: boolean;
  notes: string | null;
  source: string | null;
  booking_status: string | null;
  event_id: string | null;
  quantity?: number | null;
  unit_cost?: string | number | null;
  deposit_paid?: string | number | null;
  /** Ring-fences this line to whichever contributor funded/decided it — a paper trail, not an enforcement mechanism. */
  funded_by_contributor_id?: string | null;
  /** Joined, read-only — the contributor's name. */
  funded_by_name?: string | null;
  deposit_due_date?: string | null;
  balance_due_date?: string | null;
  revenue_amount?: string | number | null;
  vendor_booking_id?: string | null;
  vendor_booking?: { id: string; status: string | null; total_amount: string | null; currency: string | null } | null;
  payments: ApiBudgetPayment[];
}

interface ApiBudgetCategory {
  id: string;
  name: string;
  planned_amount: string | number | null;
  actual_amount: string | number | null;
  unconverted_payments?: number;
  notes: string | null;
  event_id: string | null;
  vendor_id?: string | null;
  payment_status: string | null;
  priority: string | null;
  archived_at?: string | null;
  items: ApiBudgetItem[];
}

interface ApiBudgetResponse {
  currency: string | null;
  categories: ApiBudgetCategory[];
}

// ── Transform API → local shape ──

const num = (v: unknown): number => (v == null || v === '' ? 0 : Number(v) || 0);
const numOrUndef = (v: unknown): number | undefined => (v == null || v === '' ? undefined : Number(v));

function transformApiPayment(p: ApiBudgetPayment): Payment {
  return {
    id: String(p.id),
    amount: num(p.amount),
    date: p.date ? p.date.slice(0, 10) : '',
    method: (p.method as Payment['method']) || 'bank-transfer',
    status: (p.status as Payment['status']) || 'pending',
    reference: p.reference || undefined,
    paid_by: p.paid_by || undefined,
    notes: p.notes || undefined,
    entryType: p.type === 'refund' ? 'refund' : 'payment',
    refundOfPaymentId: p.refund_of_payment_id != null ? String(p.refund_of_payment_id) : undefined,
    currency: p.currency || undefined,
  };
}

function transformApiItem(item: ApiBudgetItem, currency: string | null): VendorBudgetItem {
  const planned = num(item.planned_cost);
  const actual = num(item.actual_cost);
  const revenue = numOrUndef(item.revenue_amount);
  return {
    id: String(item.id),
    // vendor_id is the screens' key for this LINE (several lines can share one vendor); the real
    // vendor link is vendor_ref. Callers must send vendor_ref, never vendor_id, to the API.
    vendor_id: String(item.id),
    vendor_ref: item.vendor_id ?? null,
    vendor_name: item.vendor_name ?? '',
    service: item.service || 'Vendor Service',
    quantity: item.quantity != null ? Number(item.quantity) : undefined,
    unit_cost: numOrUndef(item.unit_cost),
    planned_cost: planned,
    actual_cost: actual,
    // The API already states every line in the budget's currency and counts
    // other-currency payments separately, so no conversion happens here.
    currency: item.currency ?? currency ?? undefined,
    fxRate: null,
    plannedCostBase: planned,
    actualCostBase: actual,
    fxRateMissing: false,
    unconvertedPayments: item.unconverted_payments ?? 0,
    payment_status: (item.payment_status as VendorBudgetItem['payment_status']) || 'not-started',
    payments: (item.payments || []).map(transformApiPayment),
    contract_signed: item.contract_signed,
    notes: item.notes || undefined,
    source: (item.source as VendorBudgetItem['source']) || 'manual',
    booking_status: (item.booking_status as VendorBudgetItem['booking_status']) || undefined,
    event_id: item.event_id || undefined,
    vendorBookingId: item.vendor_booking_id ?? undefined,
    vendorBookingStatus: item.vendor_booking?.status ?? undefined,
    depositAmount: numOrUndef(item.deposit_paid),
    depositDueDate: item.deposit_due_date || undefined,
    balanceDueDate: item.balance_due_date || undefined,
    lineRevenue: revenue && revenue > 0 ? revenue : undefined,
    fundedByContributorId: item.funded_by_contributor_id ?? undefined,
    fundedByName: item.funded_by_name ?? undefined,
  };
}

function transformApiCategory(cat: ApiBudgetCategory, currency: string | null): BudgetCategory {
  const vendors = (cat.items || []).map((i) => transformApiItem(i, currency));
  // A gift contribution posts as its own line (source: 'contribution'): funding
  // received, not spend, so it is kept out of the actual (alfa2267/ans-main#154).
  const expenditure = vendors.filter((v) => v.source !== 'contribution');
  const contributed = vendors.filter((v) => v.source === 'contribution');
  const actual = expenditure.reduce((sum, v) => sum + v.actual_cost, 0);
  return {
    id: String(cat.id),
    name: cat.name,
    planned_amount: num(cat.planned_amount),
    actual_amount: vendors.length > 0 ? actual : num(cat.actual_amount),
    contributed_amount: contributed.reduce((sum, v) => sum + v.actual_cost, 0),
    vendors,
    // Payments in another currency than the budget's, left out of the amounts above.
    unconvertedCount: cat.unconverted_payments ?? 0,
    notes: cat.notes || undefined,
    event_id: cat.event_id || undefined,
    event_ids: cat.event_id ? [cat.event_id] : [],
    payment_status: (cat.payment_status as BudgetCategory['payment_status']) || undefined,
    priority: (cat.priority as BudgetCategory['priority']) || undefined,
    archived: !!cat.archived_at,
  };
}

// Net paid = completed payments minus completed refunds. A refund only
// reduces the total once it's actually confirmed (status 'completed'),
// same as a payment only counts once it clears — a merely-recorded
// pending refund shouldn't yet reopen the balance as still-outstanding.
export function netPaid(payments: Payment[]): number {
  // Only payments in the budget's currency count (the API leaves the others out of actual_cost too).
  const budget = getBudgetCurrency()?.toUpperCase();
  const completed = payments.filter(p => p.status === 'completed' && (!budget || !p.currency || p.currency.toUpperCase() === budget));
  const paid = completed.filter(p => p.entryType !== 'refund').reduce((sum, p) => sum + p.amount, 0);
  const refunded = completed.filter(p => p.entryType === 'refund').reduce((sum, p) => sum + p.amount, 0);
  return paid - refunded;
}

// ── In-memory load/save ──

export function saveBudget(categories: BudgetCategory[]): void {
  budgetCache = categories;
  window.dispatchEvent(new Event(BUDGET_CHANGE_EVENT));
}

export function loadBudget(): BudgetCategory[] {
  syncProject();
  return budgetCache !== null ? budgetCache : [];
}

// ── API-first refresh ──

/**
 * Reads the whole budget (active and archived categories).
 * `strict`: rethrow an API failure instead of quietly returning the cache, so a
 * screen can tell "no budget" from "the budget API is not working".
 * A 403 means this role may not see money: it is remembered and nothing more is asked.
 */
export async function refreshBudgetFromApi(opts: { strict?: boolean } = {}): Promise<BudgetCategory[]> {
  if (!BUDGET_ENABLED) {
    if (opts.strict) throw new ApiError('Budget is switched off.', { status: 404 });
    return [];
  }
  syncProject();
  if (budgetBlocked) {
    if (opts.strict) throw new ApiError("You don't have permission to do that.", { status: 403 });
    return [];
  }
  try {
    const response = await apiGet<ApiBudgetResponse>(planningPath('/budget?lifecycle=all'));
    budgetCurrency = response.currency ?? null;
    const categories = (response.categories || []).map((c) => transformApiCategory(c, budgetCurrency));
    // Updates the cache directly rather than through saveBudget(), which
    // dispatches BUDGET_CHANGE_EVENT. App.tsx listens for that event and
    // calls this function in response — going through saveBudget() here
    // made every refresh re-trigger itself via the event, forever.
    // A refresh is syncing the cache to match the server, not a new change.
    budgetCache = categories;
    return categories;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) budgetBlocked = true;
    if (opts.strict) throw err;
    return loadBudget();
  }
}

// ── API helpers for create/update/delete ──

function categoryBody(category: BudgetCategory): Record<string, unknown> {
  return {
    name: category.name,
    planned_amount: category.planned_amount,
    notes: category.notes || null,
    event_id: (category.event_ids?.[0] ?? category.event_id) || null,
    priority: category.priority || null,
  };
}

export async function apiCreateBudgetCategory(category: BudgetCategory): Promise<BudgetCategory | null> {
  try {
    const created = await apiPost<ApiBudgetCategory>(planningPath('/budget/categories'), categoryBody(category));
    // A first category can create the budget row; pick up its currency on the next refresh.
    return transformApiCategory({ ...created, items: created.items ?? [] }, budgetCurrency);
  } catch (err) {
    console.error('API create budget category failed:', err);
    return null;
  }
}

export async function apiUpdateBudgetCategory(category: BudgetCategory): Promise<void> {
  try {
    await apiPut(planningPath(`/budget/categories/${category.id}`), categoryBody(category));
  } catch (err) {
    console.error('API update budget category failed:', err);
  }
}

async function setArchived(categoryId: string, archived: boolean): Promise<boolean> {
  const categories = loadBudget();
  const idx = categories.findIndex(c => c.id === categoryId);
  if (idx === -1) return false;
  try {
    await apiPost(planningPath(`/budget/categories/${categoryId}/${archived ? 'archive' : 'restore'}`), {});
  } catch (err) {
    console.error(`API ${archived ? 'archive' : 'restore'} budget category failed:`, err);
    return false;
  }
  const next = [...categories];
  next[idx] = { ...categories[idx], archived };
  saveBudget(next);
  return true;
}

/** Archive a budget category (archived_at on the server). */
export function archiveBudgetCategory(categoryId: string): Promise<boolean> {
  return setArchived(categoryId, true);
}

/** Restore an archived budget category. */
export function restoreBudgetCategory(categoryId: string): Promise<boolean> {
  return setArchived(categoryId, false);
}

/** Permanent delete — only allowed for already-archived categories; the server refuses one that holds payments. */
export async function deleteBudgetCategory(categoryId: string): Promise<boolean> {
  const categories = loadBudget();
  const target = categories.find(c => c.id === categoryId);
  if (!target || !target.archived) return false;
  try {
    await apiDelete(planningPath(`/budget/categories/${categoryId}`));
  } catch (err) {
    console.error('API delete budget category failed:', err);
    return false;
  }
  saveBudget(categories.filter(c => c.id !== categoryId));
  return true;
}

export async function apiDeleteBudgetCategory(categoryId: string): Promise<void> {
  try {
    await apiDelete(planningPath(`/budget/categories/${categoryId}`));
  } catch (err) {
    console.error('API delete budget category failed:', err);
  }
}

const isUuid = (v: unknown): v is string => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);

function itemBody(item: Partial<VendorBudgetItem>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  // Only the real vendor link goes to the API (vendor_id on the screens is the line's own key).
  if (item.vendor_ref !== undefined) body.vendor_id = isUuid(item.vendor_ref) ? item.vendor_ref : null;
  if (item.vendor_name !== undefined) body.vendor_name = item.vendor_name;
  if (item.service !== undefined) body.service = item.service;
  if (item.quantity !== undefined) body.quantity = item.quantity ?? null;
  if (item.unit_cost !== undefined) body.unit_cost = item.unit_cost ?? null;
  if (item.planned_cost !== undefined) body.planned_cost = item.planned_cost;
  // actual_cost is never sent: it is derived on the server from the payments.
  if (item.payment_status !== undefined) body.payment_status = item.payment_status;
  if (item.contract_signed !== undefined) body.contract_signed = item.contract_signed;
  if (item.notes !== undefined) body.notes = item.notes;
  if (item.source !== undefined) body.source = item.source;
  if (item.booking_status !== undefined) body.booking_status = item.booking_status;
  if (item.event_id !== undefined) body.event_id = isUuid(item.event_id) ? item.event_id : null;
  if (item.depositAmount !== undefined) body.deposit_paid = item.depositAmount;
  if (item.depositDueDate !== undefined) body.deposit_due_date = item.depositDueDate || null;
  if (item.balanceDueDate !== undefined) body.balance_due_date = item.balanceDueDate || null;
  if (item.vendorBookingId !== undefined) body.vendor_booking_id = isUuid(item.vendorBookingId) ? item.vendorBookingId : null;
  if (item.fundedByContributorId !== undefined) body.funded_by_contributor_id = isUuid(item.fundedByContributorId) ? item.fundedByContributorId : null;
  return body;
}

export async function apiCreateBudgetItem(categoryId: string, item: Omit<VendorBudgetItem, 'id'>): Promise<VendorBudgetItem | null> {
  if (!isUuid(categoryId)) return null;
  try {
    const created = await apiPost<ApiBudgetItem>(planningPath('/budget/items'), {
      ...itemBody({ ...item, planned_cost: Number(item.planned_cost) || 0, booking_status: item.booking_status || 'inquiry', source: item.source || 'manual' }),
      category_id: categoryId,
    });
    return transformApiItem({ ...created, payments: created.payments ?? [] }, budgetCurrency);
  } catch (err) {
    console.error('API create budget item failed:', err);
    return null;
  }
}

export async function apiUpdateBudgetItem(itemId: string, item: Partial<VendorBudgetItem>): Promise<void> {
  if (!isUuid(itemId)) {
    console.error(`apiUpdateBudgetItem: "${itemId}" is not a budget line id — pass vendor.id, not vendor.vendor_id. Save was dropped.`);
    return;
  }
  try {
    await apiPut(planningPath(`/budget/items/${itemId}`), itemBody(item));
  } catch (err) {
    console.error('API update budget item failed:', err);
  }
}

export async function apiDeleteBudgetItem(itemId: string): Promise<void> {
  if (!isUuid(itemId)) return;
  try {
    await apiDelete(planningPath(`/budget/items/${itemId}`));
  } catch (err) {
    console.error('API delete budget item failed:', err);
  }
}

/**
 * Records a payment against a budget line and throws the ApiError if the API
 * refuses it, so a form can show the field errors. The payment names its own
 * currency: the one it was given, else the budget's. With neither, the API
 * refuses it ("currency not set") rather than guessing.
 */
export async function createPaymentOrThrow(budgetItemId: string, payment: Payment): Promise<Payment> {
  const created = await apiPost<ApiBudgetPayment>(planningPath('/budget/payments'), {
    budget_item_id: budgetItemId,
    amount: payment.amount,
    currency: payment.currency ?? budgetCurrency ?? undefined,
    date: payment.date || null,
    method: payment.method || null,
    status: payment.status || 'pending',
    reference: payment.reference || null,
    paid_by: payment.paid_by || null,
    notes: payment.notes || null,
    type: payment.entryType === 'refund' ? 'refund' : 'payment',
    refund_of_payment_id: payment.refundOfPaymentId || null,
  });
  return transformApiPayment(created);
}

/** Edits a payment and throws the ApiError if the API refuses it. */
export async function updatePaymentOrThrow(paymentId: string, updates: Partial<Payment>): Promise<Payment> {
  // Only the fields that were given go to the API: leaving one out keeps its value (voiding must not erase the date, method or notes).
  const body: Record<string, unknown> = {};
  if (updates.amount !== undefined) body.amount = updates.amount;
  if (updates.currency !== undefined) body.currency = updates.currency;
  if (updates.date !== undefined) body.date = updates.date || null;
  if (updates.method !== undefined) body.method = updates.method || null;
  if (updates.status !== undefined) body.status = updates.status;
  if (updates.reference !== undefined) body.reference = updates.reference || null;
  if (updates.paid_by !== undefined) body.paid_by = updates.paid_by || null;
  if (updates.notes !== undefined) body.notes = updates.notes || null;
  const updated = await apiPatch<ApiBudgetPayment>(planningPath(`/budget/payments/${paymentId}`), body);
  return transformApiPayment(updated);
}

export async function apiCreatePayment(budgetItemId: string, payment: Payment): Promise<Payment | null> {
  if (!isUuid(budgetItemId)) return null;
  try {
    return await createPaymentOrThrow(budgetItemId, payment);
  } catch (err) {
    console.error('API create payment failed:', err);
    return null;
  }
}

export async function apiUpdatePayment(paymentId: string, updates: Partial<Payment>): Promise<Payment | null> {
  if (!isUuid(paymentId)) return null;
  try {
    return await updatePaymentOrThrow(paymentId, updates);
  } catch (err) {
    console.error('API update payment failed:', err);
    return null;
  }
}

export async function apiDeletePayment(paymentId: string): Promise<void> {
  if (!isUuid(paymentId)) return;
  try {
    await apiDelete(planningPath(`/budget/payments/${paymentId}`));
  } catch (err) {
    console.error('API delete payment failed:', err);
  }
}

// ── Pure calculations ──

export function calculateBudgetSummary(categories: BudgetCategory[]): BudgetSummary {
  const totalPlanned = categories.reduce((sum, cat) => sum + (Number(cat.planned_amount) || 0), 0);
  const totalActual = categories.reduce((sum, cat) => sum + (Number(cat.actual_amount) || 0), 0);

  // Paid = completed payments minus refunds, in the budget's currency: the same rule as the server's actual.
  const totalPaid = categories.reduce((sum, cat) => sum + cat.vendors.reduce((vs, vendor) => vs + netPaid(vendor.payments), 0), 0);

  const totalRemaining = totalPlanned - totalActual;
  const percentageSpent = totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0;

  const categoriesOverBudget = categories
    .filter(cat => cat.actual_amount > cat.planned_amount)
    .map(cat => cat.name);

  const categoriesUnderBudget = categories
    .filter(cat => cat.actual_amount < cat.planned_amount && cat.actual_amount > 0)
    .map(cat => cat.name);

  return {
    totalPlanned,
    totalActual,
    totalPaid,
    totalRemaining,
    percentageSpent,
    categoriesOverBudget,
    categoriesUnderBudget
  };
}

function getDefaultBudgetCategories(): BudgetCategory[] {
  return DEFAULT_BUDGET_CATEGORY_SEEDS.map(seed => ({
    ...seed,
    planned_amount: 0,
    actual_amount: 0,
    vendors: [],
  }));
}
